/* eslint-disable arrow-body-style */
import React, { memo, useMemo } from 'react';
import { DialerCall } from '../../../svg/dialer-call';
import { DialerSignalBars } from '../../../svg/dialer-signal-bars';
import { VerticalDialerDots } from '../../../svg/verticalDialerDots';
import { ProspectOption } from '../types';
import './_dialer-incoming-call.scss';
import { DialerEndCall } from '../../../svg/dialer-endCall';
import NetworkStrengthBars from '../../../svg/network-strength-bars';

interface DialerIncomingCallProps {
  prospect: ProspectOption;
  onAnswer?: () => void;
  onDecline?: () => void;
}

/**
 * Renders a dialer incoming call component.
 *
 * @param {DialerIncomingCallProps} props - Props passed to the component.
 * @param {ProspectOption} props.prospect - The prospect object.
 * @param {() => void} props.onAnswer - Callback function to answer the call.
 * @param {() => void} props.onDecline - Callback function to decline the call.
 * @returns {JSX.Element} - The rendered component.
 */
const DialerIncomingCall: React.FC<DialerIncomingCallProps> = ({
  prospect,
  onAnswer,
  onDecline,
}) => {
  // ** Memoized Values
  const fullName = useMemo(
    () =>
      `${prospect?.firstName?.trim() || ''} ${
        prospect?.lastName?.trim() || ''
      }`.trim() ||
      prospect?.incomingPhoneNumber ||
      '',
    [prospect?.firstName, prospect?.incomingPhoneNumber, prospect?.lastName],
  );

  const subLine = useMemo(
    () =>
      prospect?.jobTitle && prospect?.companyName
        ? `${prospect.jobTitle} at ${prospect.companyName}`
        : prospect?.incomingPhoneNumber || '-',
    [prospect?.companyName, prospect?.jobTitle, prospect?.incomingPhoneNumber],
  );

  const isUnknownProspect = useMemo(
    () => fullName?.toLowerCase() === subLine?.toLowerCase(),
    [subLine, fullName],
  );

  return (
    <div className="dialer-incoming-call-container">
      <div className="dialer-incoming-call-header">
        <div className="dialer-incoming-call-drag-handle">
          <VerticalDialerDots width={14} height={14} />
        </div>
        <NetworkStrengthBars level={4} size={18} />
        <span className="dialer-incoming-call-title">Incoming Call</span>
      </div>
      <div className="dialer-incoming-call-content">
        <div className="dialer-incoming-call-info">
          <p className="dialer-incoming-call-name">{fullName}</p>
          {!isUnknownProspect && (
            <p className="dialer-incoming-call-role">{subLine}</p>
          )}
        </div>
        <div className="dialer-incoming-call-actions">
          <button
            type="button"
            className="dialer-incoming-call-answer-btn"
            onClick={onAnswer}
            aria-label="Answer call"
          >
            <DialerCall width={24} height={24} />
          </button>
          <button
            type="button"
            className="dialer-incoming-call-decline-btn"
            onClick={onDecline}
            aria-label="Decline call"
          >
            <DialerEndCall width={24} height={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(DialerIncomingCall);
